import { useEffect, useRef, useState } from 'react';

/** Pull distance (px) at which a release triggers the refresh. */
export const PULL_THRESHOLD = 70;
/** Damping applied to the drag (the finger moves 1px, the indicator 0.6px). */
export const PULL_DAMPING = 0.6;
/** Maximum pull distance the indicator travels (px). */
export const PULL_MAX = 90;

export interface PullToRefreshState {
  /** Current damped pull distance (px) — drives the progress ring fill. */
  pullDist: number;
  /** True while the refresh callback is awaited — the ring spins. */
  refreshing: boolean;
}

/**
 * Pull-to-refresh for a scroll container (native-app feel), shared pattern
 * with the Project Portal app's PortalLayout. Fires `onRefresh` when a touch
 * drag pulls the element down from scroll-top past PULL_THRESHOLD.
 *
 * REAL touch on WebKit: the browser claims pan gestures and fires
 * pointercancel before pointermove ever arrives (the pointer-event version
 * died on the portal's device test) — so this uses non-passive TOUCH
 * listeners and preventDefaults the downward drag at scroll-top, taking the
 * gesture away from the browser for the pull. Horizontal drags and scrolls
 * from mid-content are left native.
 *
 * Inert in browsers without touch input — the listeners simply never fire
 * on a desktop mouse.
 */
export function usePullToRefresh(
  ref: React.RefObject<HTMLElement | null>,
  onRefresh: () => void | Promise<void>,
  enabled = true,
): PullToRefreshState {
  const [pullDist, setPullDist] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  // Latest callback without re-binding listeners on every render.
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;
  const pullDistRef = useRef(0);
  const pullStart = useRef<{ y: number; x: number; horizontal: boolean } | null>(null);
  // Mirror of `refreshing` readable inside the touch handlers without
  // rebinding listeners mid-gesture.
  const refreshingRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    const setPull = (d: number) => {
      pullDistRef.current = d;
      setPullDist(d);
    };

    const onStart = (e: TouchEvent) => {
      if (refreshingRef.current || e.touches.length !== 1) return;
      if (el.scrollTop <= 0) pullStart.current = { y: e.touches[0].clientY, x: e.touches[0].clientX, horizontal: false };
    };

    const onMove = (e: TouchEvent) => {
      const st = pullStart.current;
      if (!st || refreshingRef.current || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - st.x;
      const dy = e.touches[0].clientY - st.y;
      if (!st.horizontal && Math.abs(dx) > Math.abs(dy)) {
        st.horizontal = true;
        setPull(0);
        return;
      }
      if (st.horizontal) return;
      if (dy > 0) {
        // Claim the downward drag at scroll-top — without this WebKit takes
        // the gesture and the pull dies silently.
        e.preventDefault();
        setPull(Math.min(dy * PULL_DAMPING, PULL_MAX));
      } else {
        setPull(0);
      }
    };

    const onEnd = async () => {
      const st = pullStart.current;
      pullStart.current = null;
      if (!st || refreshingRef.current) return;
      if (pullDistRef.current >= PULL_THRESHOLD) {
        setPull(0);
        refreshingRef.current = true;
        setRefreshing(true);
        try {
          await refreshRef.current();
        } finally {
          refreshingRef.current = false;
          setRefreshing(false);
        }
      } else {
        setPull(0);
      }
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [ref, enabled]);

  return { pullDist, refreshing };
}
